"use client"

import { ChevronRightIcon, EyeIcon } from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import {
  ArrowThickDownIcon,
  CandlesIcon,
  CoinsIcon,
  ContactsIcon,
  DealsIcon,
  EarnIcon,
  HistoryIcon,
  HomeIcon,
  NearIcon,
  NearLogoIcon,
  RWAIcon,
  SendIcon,
  SwapIcon,
  TriangleUpIcon,
} from "@src/icons"
import clsx from "clsx"
import { motion, useReducedMotion } from "framer-motion"
import dynamic from "next/dynamic"
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import Spinner from "../Spinner"
import { ContactsContent, ContactsHeader } from "./LandingContactsPreview"
import { DealsContent, DealsHeader } from "./LandingDealsPreview"
import { HistoryContent, HistoryHeader } from "./LandingHistoryPreview"
import { LandingListPage } from "./LandingListPage"
import {
  ExploreContent,
  ExploreHeader,
  type ExploreHref,
} from "./LandingMarketsPreview"
import LandingSwapShell, { SwapHeader } from "./LandingSwapShell"
import { enterTransition, ITEM_STAGGER, ITEMS_DELAY } from "./landingMotion"

const WIDE_HREFS = new Set(["/home", "/crypto", "/rwa", "/perps", "/earn"])
const EXPLORE_HREFS = new Set<string>(["/crypto", "/rwa", "/perps", "/earn"])

const LandingSwapQuote = dynamic(() => import("./LandingSwapQuote"), {
  ssr: false,
  loading: () => (
    <LandingSwapShell hideHeader>
      <div className="-outline-offset-1 relative flex h-[287px] items-center justify-center rounded-2xl bg-gray-50 outline outline-gray-200">
        <div className="absolute inset-x-px h-px bg-gray-200" />
        <div className="-outline-offset-1 relative flex size-10 items-center justify-center rounded-full bg-gray-50 text-gray-400 outline outline-gray-200">
          <Spinner size="sm" />
        </div>
      </div>
    </LandingSwapShell>
  ),
})

type NavItemBase = {
  label: string
  shortLabel?: string
}

type NavLinkItemType = NavItemBase & {
  href: string
  icon: ComponentType<React.SVGProps<SVGSVGElement>>
  iconSolid?: ComponentType<React.SVGProps<SVGSVGElement>>
  children?: never
  showInTabs?: boolean
}

type NavGroupItemType = NavItemBase & {
  href?: never
  icon?: never
  iconSolid?: never
  children: NavLinkItemType[]
  showInTabs?: false
}

type NavItemType = NavLinkItemType | NavGroupItemType

const navItems: NavItemType[] = [
  {
    label: "Portfolio",
    href: "/home",
    icon: HomeIcon,
  },
  {
    label: "Swap",
    href: "/swap",
    icon: SwapIcon,
  },
  {
    label: "Markets",
    children: [
      {
        label: "Crypto",
        href: "/crypto",
        icon: CoinsIcon,
      },
      {
        label: "RWA",
        href: "/rwa",
        icon: RWAIcon,
      },
      {
        label: "Perps",
        href: "/perps",
        icon: CandlesIcon,
      },
      {
        label: "Earn",
        href: "/earn",
        icon: EarnIcon,
      },
    ],
  },
  {
    label: "Private deals",
    shortLabel: "Deals",
    href: "/deals",
    icon: DealsIcon,
  },
  {
    label: "History",
    href: "/history",
    icon: HistoryIcon,
  },
  {
    label: "Contacts",
    href: "/contacts",
    icon: ContactsIcon,
  },
]

export function isNavLinkItem(item: NavItemType): item is NavLinkItemType {
  return "href" in item
}

const mockCryptoPreview = [
  { symbol: "BTC", icon: "/static/icons/token/btc.svg" },
  { symbol: "USDC", icon: "/static/icons/token/usdc.svg" },
  { symbol: "NEAR", icon: "/static/icons/token/near.svg" },
]

const mockRwaPreview = [
  { symbol: "TSLA", icon: "/static/icons/market/tsla.png" },
  { symbol: "AAPL", icon: "/static/icons/market/aapl.svg" },
  { symbol: "AMZN", icon: "/static/icons/market/amzn.svg" },
]

const formatUsd = (value: number, fractionDigits = 2) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)

const mockBalances = {
  crypto: 63148.72,
  rwa: 37429.15,
  perps: 28563.4,
  earn: 21074.88,
  perpsPnl: 1736,
} as const

const mockTotalBalance =
  mockBalances.crypto +
  mockBalances.rwa +
  mockBalances.perps +
  mockBalances.earn

const mockBalanceRows: {
  label: string
  description: string
  value: string
  supportingContent: ReactNode
}[] = [
  {
    label: "Crypto",
    description: "8 assets",
    value: formatUsd(mockBalances.crypto),
    supportingContent: (
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="-space-x-1.5 flex items-center">
          {mockCryptoPreview.map(({ symbol, icon }) => (
            <AssetComboIcon
              key={symbol}
              icon={icon}
              sizeClassName="size-6"
              className="rounded-full outline-2 outline-white"
            />
          ))}
        </div>
        <span className="font-medium text-gray-500 text-sm/6">+5 more</span>
      </div>
    ),
  },
  {
    label: "RWA",
    description: "6 assets",
    value: formatUsd(mockBalances.rwa),
    supportingContent: (
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="-space-x-1.5 flex items-center">
          {mockRwaPreview.map(({ symbol, icon }) => (
            <AssetComboIcon
              key={symbol}
              icon={icon}
              sizeClassName="size-6"
              className="rounded-full outline-2 outline-white"
            />
          ))}
        </div>
        <span className="font-medium text-gray-500 text-sm/6">+3 more</span>
      </div>
    ),
  },
  {
    label: "Perps",
    description: "4 positions",
    value: formatUsd(mockBalances.perps),
    supportingContent: (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-0.5 rounded-md bg-green-600/15 pr-2 pl-1 text-green-800">
          <TriangleUpIcon className="size-4 shrink-0" />
          <div className="font-semibold text-sm/6 tracking-tight">
            {formatUsd(mockBalances.perpsPnl, 0)}
          </div>
        </div>
        <span className="font-medium text-gray-500 text-sm/6">
          unrealized P&L
        </span>
      </div>
    ),
  },
  {
    label: "Earn",
    description: "2 positions",
    value: formatUsd(mockBalances.earn),
    supportingContent: (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-0.5 rounded-md bg-sky-500/15 pr-2 pl-1 text-sky-700">
          <div className="flex size-4 shrink-0 items-center justify-center">
            <div className="size-1.5 rounded-full bg-current" />
          </div>
          <div className="font-semibold text-sm/6 tracking-tight">8.4%</div>
        </div>
        <span className="font-medium text-gray-500 text-sm/6">blended APY</span>
      </div>
    ),
  },
]

export default function LandingMockApp({
  demoStartDelay = 0,
}: {
  demoStartDelay?: number
}) {
  const [activeHref, setActiveHref] = useState("/home")
  const [swapAmount, setSwapAmount] = useState("")
  const hasSeenHome = useRef(false)
  const homeEnterDelay = hasSeenHome.current
    ? 0
    : Math.max(0, demoStartDelay - 1)

  const handleSelect = useCallback((href: string) => {
    hasSeenHome.current = true
    setActiveHref(href)
    if (href !== "/swap") {
      setSwapAmount("")
    }
  }, [])

  const reduceMotion = useReducedMotion()

  return (
    <div className="mx-auto w-full max-w-7xl overflow-visible px-6 py-16 sm:px-8 sm:pb-24 min-[1350px]:px-0!">
      <figure
        aria-labelledby="landing-mock-app-description"
        className="relative h-87 w-137.5 sm:h-174 sm:w-275 lg:mx-auto lg:w-full lg:max-w-319.5"
      >
        <motion.div
          className="absolute top-0 left-8 z-0 hidden origin-bottom lg:block"
          initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.9 }}
          animate={{ opacity: 1, y: "calc(-100% + 0.75rem)", scale: 1 }}
          transition={{
            type: "spring",
            duration: 0.5,
            bounce: 0.2,
            delay: reduceMotion ? 0 : demoStartDelay + 0.7,
          }}
        >
          <div className="flex items-center justify-center gap-2 px-4 pt-2 pb-6">
            <div className="flex size-4 items-center justify-center rounded-full bg-green-500/30">
              <div className="size-2 rounded-full bg-green-600" />
            </div>
            <p
              id="landing-mock-app-description"
              className="font-medium text-base/6 text-gray-500"
            >
              Live demo.{" "}
              <button
                type="button"
                onClick={() => handleSelect("/swap")}
                className="text-gray-900 underline"
              >
                Try a swap
              </button>{" "}
              for a real quote.
            </p>
          </div>
        </motion.div>
        <div
          aria-hidden="true"
          className="relative z-10 flex h-174 w-275 origin-top-left scale-50 overflow-hidden rounded-[20px] bg-gray-800 sm:scale-100 lg:w-full"
        >
          <aside className="flex w-60 flex-col">
            <div className="flex items-center gap-3 border-white/10 border-b px-6 py-5">
              <NearIcon className="size-6 shrink-0" />
              <NearLogoIcon className="h-3 shrink-0 text-white" />
            </div>

            <nav className="flex flex-col space-y-0.5 p-3">
              {navItems.map((item) => (
                <MockNavItem
                  key={item.label}
                  item={item}
                  activeHref={activeHref}
                  onSelect={handleSelect}
                />
              ))}
            </nav>
          </aside>

          <div className="min-w-0 flex-1 p-2 pl-0">
            <div className="h-full w-full overflow-hidden rounded-xl bg-gray-10 p-8">
              <LandingListPage
                href={activeHref}
                maxWidthClassName={
                  WIDE_HREFS.has(activeHref) ? "max-w-176" : "max-w-md"
                }
                fadeContent={activeHref === "/swap"}
                header={
                  activeHref === "/home" ? (
                    <HomeHeader />
                  ) : activeHref === "/swap" ? (
                    <SwapHeader />
                  ) : activeHref === "/deals" ? (
                    <DealsHeader />
                  ) : activeHref === "/history" ? (
                    <HistoryHeader />
                  ) : activeHref === "/contacts" ? (
                    <ContactsHeader />
                  ) : EXPLORE_HREFS.has(activeHref) ? (
                    <ExploreHeader href={activeHref as ExploreHref} />
                  ) : null
                }
              >
                {activeHref === "/swap" ? (
                  <LandingSwapQuote
                    hideHeader
                    amount={swapAmount}
                    onAmountChange={setSwapAmount}
                  />
                ) : activeHref === "/deals" ? (
                  <DealsContent key={activeHref} />
                ) : activeHref === "/history" ? (
                  <HistoryContent key={activeHref} />
                ) : activeHref === "/contacts" ? (
                  <ContactsContent key={activeHref} />
                ) : EXPLORE_HREFS.has(activeHref) ? (
                  <ExploreContent
                    key={activeHref}
                    href={activeHref as ExploreHref}
                  />
                ) : (
                  <HomeContent key={activeHref} enterDelay={homeEnterDelay} />
                )}
              </LandingListPage>
            </div>
          </div>
        </div>
      </figure>
    </div>
  )
}

function HomeHeader() {
  return (
    <header className="pb-8">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-medium text-base/6 text-gray-500">
              Total balance
            </h2>
            <span className="flex size-6 items-center justify-center text-gray-400">
              <EyeIcon className="size-4 shrink-0" />
            </span>
          </div>

          <div className="-tracking-[0.04em] mt-1 font-medium text-4xl text-gray-900">
            {formatUsd(mockTotalBalance)}
          </div>
        </div>

        <div className="flex gap-1">
          <Button size="lg" className="pointer-events-none">
            <span className="flex items-center justify-center gap-2 pr-2 pl-1">
              <ArrowThickDownIcon className="size-4" />
              Receive
            </span>
          </Button>
          <Button size="lg" className="pointer-events-none">
            <span className="flex items-center justify-center gap-2 pr-2 pl-1">
              <SendIcon className="size-4" />
              Send
            </span>
          </Button>
        </div>
      </div>
    </header>
  )
}

function HomeContent({ enterDelay = 0 }: { enterDelay?: number }) {
  const reduceMotion = useReducedMotion()
  const [entered, setEntered] = useState(false)
  const animateEnter = !reduceMotion
  const visible = entered || !animateEnter
  const innerDelay = enterDelay + ITEMS_DELAY + ITEM_STAGGER

  useEffect(() => {
    setEntered(true)
  }, [])

  return (
    <motion.div
      className="rounded-2xl border border-gray-200 bg-gray-50 px-1 pb-1"
      {...enterTransition(enterDelay + ITEMS_DELAY, animateEnter, { visible })}
    >
      <motion.div
        className="px-8 py-3"
        {...enterTransition(innerDelay, animateEnter, { y: false, visible })}
      >
        <h3 className="font-semibold text-gray-900 text-sm/5">Balances</h3>
      </motion.div>

      <ul className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200">
        {mockBalanceRows.map(
          ({ label, description, value, supportingContent }, index) => (
            <motion.li
              key={label}
              className="bg-white"
              {...enterTransition(
                innerDelay + (index + 1) * ITEM_STAGGER,
                animateEnter,
                { y: false, visible }
              )}
            >
              <div className="grid grid-cols-[1.2fr_1.2fr_1fr] px-8 py-5">
                <div className="flex flex-col">
                  <h4 className="font-semibold text-base/6 text-gray-900 tracking-tight">
                    {label}
                  </h4>
                  <p className="font-medium text-gray-500 text-sm/5">
                    {description}
                  </p>
                </div>

                <div className="flex min-h-6 items-center">
                  {supportingContent}
                </div>

                <div className="flex items-center justify-end gap-8 text-right">
                  <span className="font-semibold text-base/6 text-gray-900 tracking-tight">
                    {value}
                  </span>
                  <ChevronRightIcon className="size-5 shrink-0 text-gray-900" />
                </div>
              </div>
            </motion.li>
          )
        )}
      </ul>
    </motion.div>
  )
}

function MockNavItem({
  item,
  activeHref,
  onSelect,
}: {
  item: NavItemType
  activeHref: string
  onSelect: (href: string) => void
}) {
  if (!isNavLinkItem(item)) {
    return (
      <div className="my-7 flex flex-col gap-1.5">
        <div className="pl-3.5 font-semibold text-sm/5 text-white/50">
          {item.label}
        </div>

        <div className="flex flex-col gap-0.5">
          {item.children.map((child) => (
            <MockNavButton
              key={child.label}
              item={child}
              isActive={activeHref === child.href}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <MockNavButton
      item={item}
      isActive={activeHref === item.href}
      onSelect={onSelect}
    />
  )
}

function MockNavButton({
  item,
  isActive,
  onSelect,
}: {
  item: NavLinkItemType
  isActive: boolean
  onSelect: (href: string) => void
}) {
  const { href, icon: Icon, label } = item

  return (
    <button
      type="button"
      onClick={() => onSelect(href)}
      className={clsx(
        "focus-visible:-outline-offset-2 flex w-full items-center gap-3 rounded-xl p-3 text-left text-gray-300 hover:bg-white/6 hover:text-white focus-visible:outline-2 focus-visible:outline-white",
        isActive && "bg-white/6 text-white"
      )}
    >
      <Icon className="size-5 shrink-0" />
      <span className="flex-1 font-semibold text-sm/5">{label}</span>
    </button>
  )
}
